import { MenuItem } from "@mui/material";
import { Form } from "formik";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as yup from "yup";
import { getArts } from "../../../../actions/arts";
import FormikDatePicker from "../../../commons/formik/formikDatePicker";
import FormikForm from "../../../commons/formik/formikForm";
import FormikSelect from "../../../commons/formik/formikSelect";
import FormikTextField from "../../../commons/formik/formikTextField";
import { StyledNovedadesSearchForm } from "../../../styled/connectivity";

const validationSchema = yup.object().shape({
    art: yup.string().required("ART es obligatoria"),
    siniestro: yup
        .string()
        .matches(/^[0-9]*$/, "Ingrese sólo números")
        .test('exclusive-filter', 'Debe completar solo uno: Siniestro o DNI', function (value) {
            const { dni } = this.parent;
            const hasValue = value && value.trim();
            const hasDni = dni && dni.trim();

            if (hasValue && hasDni) {
                return false;
            }
            return true;
        }),
    dni: yup
        .string()
        .matches(/^[0-9]*$/, "Ingrese sólo números")
        .test('exclusive-filter', 'Debe completar solo uno: Siniestro o DNI', function (value) {
            const { siniestro } = this.parent;
            const hasValue = value && value.trim();
            const hasSiniestro = siniestro && siniestro.trim();

            if (hasValue && hasSiniestro) {
                return false;
            }
            return true;
        }),
    fechaDesde: yup.date()
        .nullable()
        .test('both-dates-required', 'Si ingresa Fecha Desde, debe ingresar Fecha Hasta', function (value) {
            const { fechaHasta } = this.parent;
            if (value && !fechaHasta) {
                return false;
            }
            return true;
        }),
    fechaHasta: yup.date()
        .nullable()
        .test('both-dates-required', 'Si ingresa Fecha Hasta, debe ingresar Fecha Desde', function (value) {
            const { fechaDesde } = this.parent;
            if (value && !fechaDesde) {
                return false;
            }
            return true;
        })
        .test('is-after-fechaDesde', 'La fecha hasta debe ser mayor o igual a la fecha desde', function (value) {
            const { fechaDesde } = this.parent;
            if (fechaDesde && value) {
                return new Date(value) >= new Date(fechaDesde);
            }
            return true;
        }),
}).test('at-least-one', 'Debe completar al menos uno: Siniestro o DNI', function (values) {
    const { siniestro, dni } = values;
    const hasSiniestro = siniestro && siniestro.trim();
    const hasDni = dni && dni.trim();

    return hasSiniestro || hasDni;
});

const initialValues = {
    art: '',
    siniestro: '',
    dni: '',
    fechaDesde: null,
    fechaHasta: null,
};

const ProvidersResponseSearch = ({ isSearching, handleSearch, handleClear }) => {
    const dispatch = useDispatch();
    const arts = useSelector((state) => state.arts.list);
    const loading = useSelector((state) => state.arts.isFetching);
    const roles = useSelector((state) => state.auth.roles);

    useEffect(() => {
        if ((roles.SOPORTE || roles.ADMIN) && arts.length === 0 && !loading) {
            dispatch(getArts());
        }
    }, [dispatch, arts.length, loading, roles.SOPORTE, roles.ADMIN]);

    const handleSubmitSearch = (values) => {
        handleSearch(values);
    };

    const handleClearForm = (resetForm) => {
        resetForm();
        if (handleClear) {
            handleClear();
        }
    };

    return (
        <div>
            <FormikForm
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmitSearch}>
                {({ resetForm, setFieldValue, values }) => (
                    <Form>
                        <StyledNovedadesSearchForm.StyledPaper>
                            <StyledNovedadesSearchForm.StyledInput>
                                <FormikSelect
                                    name="art"
                                    label="Art"
                                    variant="standard"
                                    value={values.art}>
                                    {arts.map((art) => {
                                        return (
                                            <MenuItem value={art.id} key={art.id}>
                                                {art.nombre}
                                            </MenuItem>
                                        );
                                    })}
                                </FormikSelect>
                            </StyledNovedadesSearchForm.StyledInput>
                            <StyledNovedadesSearchForm.StyledInput>
                                <FormikTextField
                                    name="siniestro"
                                    label="Siniestro"
                                    variant="standard"
                                    inputProps={{
                                        onBlur: (e) => {
                                            setFieldValue('siniestro', e.target.value.trim());
                                        },
                                        onKeyDown: (e) => {
                                            if (e.key === ' ') {
                                                e.preventDefault();
                                            }
                                        }
                                    }}
                                    disabled={!!values.dni}
                                />
                            </StyledNovedadesSearchForm.StyledInput>
                            <StyledNovedadesSearchForm.StyledInput>
                                <FormikTextField
                                    name="dni"
                                    label="DNI"
                                    variant="standard"
                                    inputProps={{
                                        onBlur: (e) => {
                                            setFieldValue('dni', e.target.value.trim());
                                        },
                                        onKeyDown: (e) => {
                                            if (e.key === ' ') {
                                                e.preventDefault();
                                            }
                                        }
                                    }}
                                    disabled={!!values.siniestro}
                                />
                            </StyledNovedadesSearchForm.StyledInput>
                            <StyledNovedadesSearchForm.StyledInput>
                                <FormikDatePicker
                                    name="fechaDesde"
                                    label="Fecha Desde"
                                    variant="standard"
                                />
                            </StyledNovedadesSearchForm.StyledInput>
                            <StyledNovedadesSearchForm.StyledInput>
                                <FormikDatePicker
                                    name="fechaHasta"
                                    label="Fecha Hasta"
                                    variant="standard"
                                />
                            </StyledNovedadesSearchForm.StyledInput>

                            <StyledNovedadesSearchForm.ButtonContainer>
                                <StyledNovedadesSearchForm.StyledButton
                                    variant="contained"
                                    type="button"
                                    onClick={() => handleClearForm(resetForm)}
                                    disabled={isSearching}>
                                    Limpiar
                                </StyledNovedadesSearchForm.StyledButton>
                                <StyledNovedadesSearchForm.StyledButton
                                    variant="contained"
                                    type="submit"
                                    color="primary"
                                    disabled={isSearching}>
                                    Buscar
                                </StyledNovedadesSearchForm.StyledButton>
                            </StyledNovedadesSearchForm.ButtonContainer>
                        </StyledNovedadesSearchForm.StyledPaper>
                    </Form>
                )}
            </FormikForm>
        </div>
    );
};

export default ProvidersResponseSearch;
